import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, DollarSign, CheckCircle, Star, CreditCard, Coins, Banknote, PiggyBank, Wallet } from 'lucide-react';
import { Button } from './components/ui/button';
import CalendarModal from './components/CalendarModal';
import { LeadCaptureModal } from './components/LeadCaptureModal';

// Bank offer cards data for the collage
const bankOffers = [
  { 
    bank: 'Chase', 
    offer: '$300 Bonus', 
    requirement: 'Open + Direct Deposit',
    color: 'from-blue-600 to-blue-700',
    textColor: 'text-white'
  },
  { 
    bank: 'Wells Fargo', 
    offer: '$425 Bonus', 
    requirement: '$500 Deposit',
    color: 'from-red-600 to-red-700',
    textColor: 'text-white'
  },
  { 
    bank: 'U.S. Bank', 
    offer: '$400 Bonus', 
    requirement: 'New Account',
    color: 'from-blue-800 to-blue-900',
    textColor: 'text-white'
  },
  { 
    bank: 'Bank of America', 
    offer: '$500 Bonus', 
    requirement: 'Qualify & Deposit',
    color: 'from-red-700 to-red-800',
    textColor: 'text-white'
  },
  { 
    bank: 'Citi', 
    offer: '$325 Bonus', 
    requirement: 'Direct Deposit',
    color: 'from-blue-500 to-blue-600',
    textColor: 'text-white'
  },
  { 
    bank: 'Capital One', 
    offer: '$250 Bonus', 
    requirement: 'Direct Deposit',
    color: 'from-orange-600 to-orange-700',
    textColor: 'text-white'
  },
];

const testimonials = [
  {
    quote: "Thousands of dollars in bonuses, simple and transparent.",
    author: "Koby"
  },
  {
    quote: "Best financial move I made this year.",
    author: "Mark"
  }
];

const features = [
  "100% legitimate and backed by actual banks",
  "Your money stays safe and accessible",
  "Clear system, no guesswork",
  "Step by step guidance and support",
  "No hidden fees or tricks"
];

const guarantees = [
  "FDIC Insured Banks",
  "Verified Offers",
  "Money Back Guarantee"
];

function App() {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isLeadCaptureOpen, setIsLeadCaptureOpen] = useState(false);

  const openCalendar = () => {
    setIsCalendarOpen(true);
  };

  const smoothScrollTo = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Add CSS for infinite scroll animation and smooth scrolling
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      html {
        scroll-behavior: smooth;
      }
      @keyframes scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .animate-scroll {
        animation: scroll 30s linear infinite;
      }
      .animate-scroll:hover {
        animation-play-state: paused;
      }
      @keyframes scroll-ticker {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .animate-scroll-ticker {
        animation: scroll-ticker 20s linear infinite;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Show lead capture popup after 5 seconds (for testing)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Temporarily disable localStorage check for testing
      // const hasSeenPopup = localStorage.getItem('hasSeenLeadCapture');
      // if (!hasSeenPopup) {
        setIsLeadCaptureOpen(true);
        // localStorage.setItem('hasSeenLeadCapture', 'true');
      // }
    }, 5000); // 5 seconds for testing

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/Fluid Logo Square Off Black Inverse Rounded Bank Site.png" 
                alt="Fluid Banks Logo" 
                className="h-8 w-8 rounded-full shadow-md"
              />
              <span className="text-lg font-semibold text-gray-900">Fluid Banks</span>
            </div>
            
            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-2">
              <button 
                onClick={() => smoothScrollTo('bank-offers')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                Bank Offers
              </button>
              <button 
                onClick={() => smoothScrollTo('how-it-works')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                How it Works
              </button>
              <button 
                onClick={() => smoothScrollTo('why-people-love-it')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                What People Say
              </button>
              <button 
                onClick={() => smoothScrollTo('testimonials')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                Testimonials
              </button>
              <button 
                onClick={() => smoothScrollTo('your-next-step')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                Next Steps
              </button>
              <button 
                onClick={() => smoothScrollTo('faqs')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 text-sm font-medium transition-all duration-200 px-4 py-2 rounded-full cursor-pointer"
              >
                FAQs
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={openCalendar}
                className="text-white px-4 py-2 rounded-full font-medium transition-colors text-sm"
                style={{ backgroundColor: '#000000' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#333333'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#000000'}
              >
                Book A Call
              </Button>
              <Button
                onClick={() => window.open('https://www.fluidbonuses.com/register', '_blank')}
                className="text-white px-6 py-2 rounded-full font-medium transition-colors"
                style={{ backgroundColor: '#5e8590' }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#4a6b75'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#5e8590'}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Split Hero Section */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content Card */}
            <div className="relative h-fit">
              <div className="bg-gray-50 rounded-3xl shadow-2xl overflow-hidden relative p-8 lg:p-12">
                {/* Financial Icons Background Pattern */}
                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                  {Array.from({ length: 10 }).map((_, rowIndex) => (
                    Array.from({ length: 8 }).map((_, colIndex) => {
                      const icons = [DollarSign, Coins, CreditCard, Banknote, PiggyBank, Wallet];
                      const iconIndex = (rowIndex + colIndex) % icons.length;
                      const Icon = icons[iconIndex];
                      return (
                        <Icon
                          key={`${rowIndex}-${colIndex}`}
                          className="absolute text-gray-400 opacity-20"
                          size={24}
                          style={{
                            top: `${(rowIndex * 10) % 100}%`,
                            left: `${(colIndex * 12.5) % 100}%`,
                            transform: `rotate(${(iconIndex * 45 + rowIndex * 30 + colIndex * 15) % 360}deg)`
                          }}
                        />
                      );
                    })
                  ))}
                </div>
                
                <div className="relative z-10">
                  <motion.h1 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                  >
                    Earn <span className="bg-gradient-to-r from-green-600 bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, #16a34a, #5e8590)' }}>$300+</span><br />
                    <span className="text-gray-800">Every Month</span><br />
                    <span className="text-xl md:text-2xl lg:text-3xl text-gray-600 font-normal">From Bank Promotions</span>
                  </motion.h1>
                  
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed"
                  >
                    Banks pay you to open accounts. Learn how to collect hundreds every month with FDIC insured accounts.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="flex flex-col sm:flex-row items-center gap-4"
                  >
                    <Button 
                      onClick={openCalendar}
                      size="lg" 
                      className="text-white text-lg px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                      style={{ backgroundColor: '#000000' }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#333333'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#000000'}
                    >
                      Book A Call
                    </Button>
                    <Button 
                      onClick={() => window.open('https://www.fluidbonuses.com/register', '_blank')}
                      size="lg" 
                      className="text-white text-lg px-10 py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                      style={{ backgroundImage: 'linear-gradient(to right, #5e8590, #4a6b75)' }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundImage = 'linear-gradient(to right, #4a6b75, #3a555f)'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundImage = 'linear-gradient(to right, #5e8590, #4a6b75)'}
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Right Side - Video */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <div className="w-full h-80">
                  <iframe
                    src="https://www.youtube.com/embed/cTPu8AVQHP8"
                    title="Bank Bonus Strategy Video"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Bank Offers Carousel Section */}
      <section id="bank-offers" className="py-8 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-1">Featured Bank Bonuses</h3>
            <p className="text-sm text-gray-600">Real offers from top banks</p>
          </div>
          
          {/* Infinite Scrolling Carousel */}
          <div className="relative">
            <div className="flex animate-scroll">
              {/* First set of cards */}
              {bankOffers.map((offer, index) => (
                <motion.div
                  key={`first-${offer.bank}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex-shrink-0 w-56 mx-3 bg-gradient-to-br ${offer.color} ${offer.textColor} transition-all duration-300 transform hover:scale-105 hover:-translate-y-1`}
                  style={{
                    borderRadius: '20px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <div className="p-5 relative overflow-hidden">
                    {/* Material 3 Surface Tint */}
                    <div className="absolute inset-0 bg-white/5 rounded-[20px]"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center p-1.5">
                          <img 
                            src={
                              offer.bank === 'Chase' ? 'https://www.chase.com/etc/designs/chase-ux/css/img/newheader/chase-logo.svg' :
                              offer.bank === 'Wells Fargo' ? 'https://www.google.com/s2/favicons?domain=wellsfargo.com&sz=64' :
                              offer.bank === 'U.S. Bank' ? 'https://www.google.com/s2/favicons?domain=usbank.com&sz=64' :
                              offer.bank === 'Bank of America' ? 'https://www.bankofamerica.com/content/dam/boaml/bofa/images/logos/bank-of-america-logo.svg' :
                              offer.bank === 'Citi' ? 'https://www.google.com/s2/favicons?domain=citibank.com&sz=64' :
                              offer.bank === 'Capital One' ? 'https://ecm.capitalone.com/WCM/card/products/common-images/capital-one-logo.svg' : 
                              'https://www.google.com/s2/favicons?domain=chase.com&sz=64'
                            }
                            alt={`${offer.bank} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              // Fallback to dollar sign if logo fails to load
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          />
                          <DollarSign className="w-4 h-4 hidden" />
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-full px-2 py-1">
                          <span className="text-xs font-medium">Limited Time</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="font-bold text-2xl tracking-tight">{offer.offer}</div>
                        <div className="font-semibold text-lg opacity-95">{offer.bank}</div>
                        <div className="text-xs opacity-80 leading-relaxed">{offer.requirement}</div>
                      </div>
                    </div>
                    
                    {/* Material 3 Decorative Elements */}
                    <div className="absolute -top-2 -right-2 w-16 h-16 bg-white/10 rounded-full blur-lg"></div>
                    <div className="absolute -bottom-3 -left-3 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
                  </div>
                </motion.div>
              ))}
              
              {/* Duplicate set for seamless loop */}
              {bankOffers.map((offer, index) => (
                <motion.div
                  key={`second-${offer.bank}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`flex-shrink-0 w-56 mx-3 bg-gradient-to-br ${offer.color} ${offer.textColor} transition-all duration-300 transform hover:scale-105 hover:-translate-y-1`}
                  style={{
                    borderRadius: '20px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <div className="p-5 relative overflow-hidden">
                    {/* Material 3 Surface Tint */}
                    <div className="absolute inset-0 bg-white/5 rounded-[20px]"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center p-1.5">
                          <img 
                            src={
                              offer.bank === 'Chase' ? 'https://www.chase.com/etc/designs/chase-ux/css/img/newheader/chase-logo.svg' :
                              offer.bank === 'Wells Fargo' ? 'https://www.google.com/s2/favicons?domain=wellsfargo.com&sz=64' :
                              offer.bank === 'U.S. Bank' ? 'https://www.google.com/s2/favicons?domain=usbank.com&sz=64' :
                              offer.bank === 'Bank of America' ? 'https://www.bankofamerica.com/content/dam/boaml/bofa/images/logos/bank-of-america-logo.svg' :
                              offer.bank === 'Citi' ? 'https://www.google.com/s2/favicons?domain=citibank.com&sz=64' :
                              offer.bank === 'Capital One' ? 'https://ecm.capitalone.com/WCM/card/products/common-images/capital-one-logo.svg' : 
                              'https://www.google.com/s2/favicons?domain=chase.com&sz=64'
                            }
                            alt={`${offer.bank} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              // Fallback to dollar sign if logo fails to load
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          />
                          <DollarSign className="w-4 h-4 hidden" />
                        </div>
                        <div className="bg-white/15 backdrop-blur-sm rounded-full px-2 py-1">
                          <span className="text-xs font-medium">Limited Time</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="font-bold text-2xl tracking-tight">{offer.offer}</div>
                        <div className="font-semibold text-lg opacity-95">{offer.bank}</div>
                        <div className="text-xs opacity-80 leading-relaxed">{offer.requirement}</div>
                      </div>
                    </div>
                    
                    {/* Material 3 Decorative Elements */}
                    <div className="absolute -top-2 -right-2 w-16 h-16 bg-white/10 rounded-full blur-lg"></div>
                    <div className="absolute -bottom-3 -left-3 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">How It Works</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e1eaec' }}>
                <span className="text-2xl font-bold" style={{ color: '#5e8590' }}>1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Fill Out Our Short Form</h3>
              <p className="text-gray-600">We learn your goals and banking profile to send you legitimate offers straight from top banks.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Get Approved</h3>
              <p className="text-gray-600">Apply for bank accounts and get approved for the bonuses we recommend for you.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Fund Your First Account</h3>
              <p className="text-gray-600">Deposit $5,000 of your own money, it stays yours, fully accessible at all times.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">We Handle The Rest</h3>
              <p className="text-gray-600">We track deadlines, complete direct deposits, and ensure compliance while you earn.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">5</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Stay Involved</h3>
              <p className="text-gray-600">Spend 15-30 minutes twice a month following our guidance, then repeat the process for new bonuses.</p>
            </motion.div>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => window.open('https://www.fluidbonuses.com/register', '_blank')}
              className="text-white text-lg px-10 py-3 rounded-full shadow-lg transition-all duration-300"
              style={{ backgroundColor: '#5e8590' }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#4a6b75'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#5e8590'}
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Why This Works Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Why This Works</h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-gray-700 mb-6">
                Banks spend millions every month to attract new customers. Instead of pouring that into ads, they pay you.
              </p>
              <p className="text-xl text-gray-700 mb-6">
                This process is known as bank churning, and when done right, it's one of the safest ways to earn real monthly cash bonuses.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-lg font-semibold text-green-800">Your initial $5,000 deposit is fully guaranteed and never at risk.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why People Love It Section */}
      <section id="why-people-love-it" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-12">Why People Love It</h2>
            
            <div className="space-y-8">
              {/* First row - 3 items */}
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {features.slice(0, 3).map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center space-x-3 text-center"
                  >
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
              
              {/* Second row - 2 items centered */}
              <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                {features.slice(3, 5).map((feature, index) => (
                  <motion.div
                    key={index + 3}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center space-x-3 text-center"
                  >
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={openCalendar}
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-3 rounded-full shadow-lg transition-all duration-300"
            >
              Start Your Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-12">Testimonials</h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-lg shadow-lg border"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-lg text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                  <p className="font-semibold text-gray-900">— {testimonial.author}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Guarantees */}
          <div className="text-center mb-12">
            <div className="flex flex-wrap justify-center gap-8">
              {guarantees.map((guarantee, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <span className="text-lg font-semibold text-gray-700">{guarantee}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Your Next Step Section */}
      <section id="your-next-step" className="py-16 text-white" style={{ backgroundColor: '#5e8590' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Your Next Step</h2>
          <p className="text-xl mb-4">Turn $5,000 into a super high yield savings account.</p>
          <p className="text-lg mb-6">This isn't investing, it's using marketing budgets from banks that want your business.</p>
          <p className="text-lg mb-12">We'll guide you through it, safely and efficiently.</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={openCalendar}
              className="bg-white hover:bg-gray-100 text-lg px-10 py-3 rounded-full shadow-lg transition-all duration-300"
              style={{ color: '#5e8590' }}
            >
              Book A Free Call
            </Button>
            <Button 
              onClick={() => window.open('https://www.fluidbonuses.com/register', '_blank')}
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-10 py-3 rounded-full shadow-lg transition-all duration-300"
            >
              Start Earning Bonuses
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faqs" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about our bank bonus service</p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: "It seems too complicated",
                answer: "We handle all the complexity for you. You just need to spend 15-30 minutes each month following our guidance, and we take care of tracking deadlines, requirements, and compliance."
              },
              {
                question: "How much does this cost?",
                answer: "We charge 33% of every bonus we help you earn."
              },
              {
                question: "Couldn't I just do this myself?",
                answer: "Yes, but are you doing it yourself? Most people don't want to invest the time or energy to do this themselves, and many find it difficult to keep switching their main work direct deposits. If you have the time and energy, we encourage you to do this yourself and get that money :)"
              },
              {
                question: "I don't trust you",
                answer: "While we're happy to meet you personally over zoom and answer any questions you have, there's no pressure at all and you should feel free to do this on your own if you aren't comfortable working with us."
              },
              {
                question: "How long does it take?",
                answer: "Each bonus takes up to a few months to earn. You'll be working toward 1-2 new bonuses each month, so after a few months, the bonuses should arrive pretty consistently."
              },
              {
                question: "I don't have the money",
                answer: "If you don't have $5,000 liquid that you can afford to put toward this strategy, we don't recommend it for you."
              },
              {
                question: "I want to see other people who have done it",
                answer: "We're happy to share how many customers we have with you. If you want to wait until you hear a friend worked with us, that's totally understandable."
              },
              {
                question: "Is this too good to be true?",
                answer: "This is not a get rich quick scheme. You can make $3,000-$4,000 a year, but not much more, and it takes consistent effort every month. Sometimes you open an account and the bank won't give out the bonus on a technicality, so it's not perfect. That said, when the consistent effort is put in, it's a reliable way to earn some extra money. The worst case is if you don't get approved for accounts or earn any bonuses, you still get your $5,000 back. It's never invested in anything, just held in cash accounts in your name, so your initial deposit is not at risk."
              },
              {
                question: "My wife won't let me do this",
                answer: "Then you probably shouldn't be here."
              },
              {
                question: "I don't want to give you my social",
                answer: "We use industry-standard Supabase databases to store customer data, and sensitive data like SSNs are encrypted. Since legally you'll be considered an independent contractor, unfortunately we have to collect your SSN."
              },
              {
                question: "Does it only work for $5k?",
                answer: "You can do it on your own for less, although it's hard to earn this much with lower deposits."
              },
              {
                question: "What about my credit score?",
                answer: "Bank accounts don't impact your credit score as long as you opt out of overdraft protection when you open new accounts (which we highly recommend opting out of)."
              },
              {
                question: "I'm worried this will affect my bank",
                answer: "Upon signing up, we ask for a list of banks you'd like us to ignore for you, and we don't send you offers for that bank so that your current banking will not be disrupted."
              },
              {
                question: "What if you steal my money?",
                answer: "All money is held in US bank accounts that are legally in your name that you have access to. We do not hold customer funds. If we steal from you, then please sue us. We have zero incentive to ruin our business and have a legal obligation not to steal. We also legally guarantee your initial deposit."
              },
              {
                question: "How is this taxed?",
                answer: "This is legally considered a side-hustle of yours and you will owe income taxes on your net earnings (bonuses earned minus our fee)."
              },
              {
                question: "How much time does this take?",
                answer: "This requires about 15-30mins of focused time each month. It's not a lot of time, but that is required."
              },
              {
                question: "Will I get debanked?",
                answer: "If you open too many accounts in too short of a time frame, it will temporarily get harder to open new bank accounts. We try to keep you under this threshold and will pause for a few months if you're having difficulties."
              },
              {
                question: "What happens if something goes wrong?",
                answer: "Text us! You'll have our WhatsApp info."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-sm border border-gray-200"
              >
                <button
                  onClick={() => {
                    const element = document.getElementById(`faq-${index}`);
                    const arrow = document.getElementById(`arrow-${index}`);
                    if (element && arrow) {
                      if (element.style.display === 'none' || element.style.display === '') {
                        element.style.display = 'block';
                        arrow.style.transform = 'rotate(180deg)';
                      } else {
                        element.style.display = 'none';
                        arrow.style.transform = 'rotate(0deg)';
                      }
                    }
                  }}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronRight 
                    id={`arrow-${index}`}
                    className="w-5 h-5 text-gray-500 transition-transform duration-200" 
                    style={{ transform: 'rotate(90deg)' }}
                  />
                </button>
                <div 
                  id={`faq-${index}`}
                  style={{ display: 'none' }}
                  className="px-6 pb-4"
                >
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-6">Disclaimer</h3>
            <div className="max-w-4xl mx-auto text-sm text-gray-600 space-y-2">
              <p>Your $5,000 stays fully accessible in your own bank accounts.</p>
              <p>Offers vary by financial institution and availability.</p>
              <p>We respect your privacy, your information is never shared or sold.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance Disclaimer Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-500 max-w-4xl mx-auto">
              Past performance does not guarantee future results, but our clients consistently experience strong monthly returns.
            </p>
          </div>
        </div>
      </section>

      {/* Calendar Modal */}
      <CalendarModal 
        isOpen={isCalendarOpen} 
        onClose={() => setIsCalendarOpen(false)} 
      />

      {/* Lead Capture Modal */}
      <LeadCaptureModal 
        isOpen={isLeadCaptureOpen} 
        onClose={() => setIsLeadCaptureOpen(false)} 
      />
    </div>
  );
}

export default App;
